import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { CitationItem, CitationWithId } from '@sciflow/cite';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounce,
  debounceTime,
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { ReferenceManagementService } from '../../services/reference-management.service';
import { selectCitation, selectReferenceList } from '../../store/references.reducer';

interface CitationItemForUI extends CitationItem {
  /** The rendered string this item's reference */
  plainReference: string;
}

interface CitationForUI extends CitationWithId {
  citationItems: CitationItemForUI[];
  citationMode?: 'generated' | 'custom';
}

type CitationItemFormGroup = FormGroup<{
  id: FormControl<string>;
  plainReference: FormControl<string>;
  label: FormControl<string>;
  locator: FormControl<string>;
  prefix: FormControl<string>;
  suffix: FormControl<string>;
  'suppress-author': FormControl<boolean>;
  format: FormControl<string>;
}>;

type CitationFormGroup = FormGroup<{
  citationID: FormControl<string>;
  citationMode: FormControl<'generated' | 'custom'>;
  plainCitation: FormControl<string>;
  plainCitationHtml: FormControl<string>;
  citationItems: FormArray<CitationItemFormGroup>;
}>;

@Component({
    selector: 'lib-citation-editor',
    templateUrl: './citation-editor.component.html',
    styleUrls: ['./citation-editor.component.scss'],
    standalone: false
})
export class CitationEditorComponent implements AfterViewInit, OnDestroy {
  stop$ = new Subject<void>();
  readonly citationLabelSuggestions = [
    'page',
    'chapter',
    'figure',
    'issue',
    'volume',
    'section',
    'paragraph',
    'line',
  ];
  private isHydratingCitationForm = false;
  private previousCitationMode: 'generated' | 'custom' = 'generated';
  private initialCustomCitationText = '';
  private initialCustomCitationHtml = '';
  private currentCitationProperties: CitationWithId['properties'] = { noteIndex: 0 };
  private expandedPanelIndices = new Set<number>([0]);
  private previewRequestId = 0;
  isCustomTextDirty: boolean = false;
  formattedCitationSuggestion: string | null = null;
  generatedPreviewText = '';

  /** A search string to filter the reference search */
  searchText$ = new BehaviorSubject<string>('');

  citation$ = this.activatedRoute.params.pipe(
    map(({ citationID }) => citationID),
    switchMap((citationID: string) => this.store.select(selectCitation(citationID)))
  );

  /** All filtered references */
  references$ = combineLatest([
    this.searchText$.pipe(debounceTime(50)), this.store.select(selectReferenceList), this.citation$
  ]).pipe(map(([searchText, references, citation]) => {
    if (!searchText || searchText.length === 0) {
      return references
        // filter out already used references
        .filter(ref => !citation?.citationItems?.some(item => item.id === ref.csl?.id));
    }
    return references
      .filter(ref => ref.bib?.toLowerCase().includes(searchText.toLowerCase()) || ref.csl?.id?.toLowerCase().includes(searchText.toLowerCase()))
      .filter(ref => !citation?.citationItems?.some(item => item.id === ref.csl?.id));
  }));

  citationFormGroup: CitationFormGroup = new FormGroup({
    citationID: new FormControl<string>('', { nonNullable: true }),
    citationMode: new FormControl<'generated' | 'custom'>('generated', { nonNullable: true }),
    plainCitation: new FormControl<string>('', { nonNullable: true }),
    plainCitationHtml: new FormControl<string>('', { nonNullable: true }),
    citationItems: new FormArray<CitationItemFormGroup>([]),
  });

  /** The citation with all citation data filled in */
  citationWithReferences$ = this.citation$.pipe(
    filter(citation => citation !== undefined),
    // we wait until at least one reference is loaded
    debounce(() => this.store.select(selectReferenceList).pipe(filter(r => r.length > 0))),
    withLatestFrom(this.store.select(selectReferenceList)),
    map(([incomingCitation, references]): CitationForUI | undefined => {
      let citation = { ...incomingCitation } as CitationForUI;
      if (citation?.citationItems) {
        // Merge citationItems changes with references
        citation.citationItems = citation?.citationItems.map(citationItem => {
          const reference = references.find(ref => ref.csl?.id === citationItem.id);
          if (!reference) { return citationItem };
          return {
            ...citationItem,
            plainReference: reference.bib ?? '',
            itemData: reference?.csl
          };
        });
      }

      return citation;
    })
  ) as Observable<CitationForUI>;

  get citationItems(): FormArray<CitationItemFormGroup> {
    return this.citationFormGroup.controls.citationItems;
  }

  get citationID(): FormControl<string> {
    return this.citationFormGroup.controls.citationID;
  }

  get plainCitation(): FormControl<string> {
    return this.citationFormGroup.controls.plainCitation;
  }

  get citationMode(): FormControl<'generated' | 'custom'> {
    return this.citationFormGroup.controls.citationMode;
  }

  get plainCitationHtml(): FormControl<string> {
    return this.citationFormGroup.controls.plainCitationHtml;
  }

  constructor(private referencesService: ReferenceManagementService, private store: Store, private router: Router,
              private activatedRoute: ActivatedRoute, private snackBar: MatSnackBar) {
  }

  trackByFn(index: number): number {
    return index;
  }

  routeToReferencesList(): void {
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
  }

  deleteReference(index): void {
    this.citationItems.removeAt(index);
    this.reindexExpandedPanelsAfterDelete(index);
  }

  resetCitationTextOverride(): void {
    this.updateCitationTextForm(this.initialCustomCitationText, this.initialCustomCitationHtml, false);
    this.updateCustomCitationDirtyState();
  }

  onCitationModeTabChange(index: number): void {
    const nextMode: 'generated' | 'custom' = index === 1 ? 'custom' : 'generated';
    if (this.citationMode.value === nextMode) {
      return;
    }
    this.citationMode.setValue(nextMode);
  }

  /** Applies current citation editor values to the document citation node.
   *  Custom text from the form is only persisted when `applyCustomText` is true
   *  (i.e. the user explicitly clicks "Apply Custom text"). All other callers
   *  send the last-applied baseline text so in-progress edits aren't leaked. */
  async applyCitationUpdate(options?: { preserveNodeContent?: boolean; applyCustomText?: boolean }): Promise<void> {
    if (this.isHydratingCitationForm) {
      return;
    }

    const updatedCitation = this.citationFormGroup.getRawValue();
    const useFormText = options?.applyCustomText === true;
    const citationPayload: CitationWithId & { plainCitationHtml?: string | null } = {
      citationID: updatedCitation.citationID,
      citationMode: updatedCitation.citationMode,
      plainCitation: useFormText ? updatedCitation.plainCitation : this.initialCustomCitationText,
      plainCitationHtml: useFormText ? updatedCitation.plainCitationHtml : this.initialCustomCitationHtml,
      citationItems: updatedCitation.citationItems.map((item) => ({
        id: item.id,
        label: item.label || undefined,
        locator: item.locator || undefined,
        prefix: item.prefix || undefined,
        suffix: item.suffix || undefined,
        'suppress-author': item['suppress-author'] || undefined,
      })),
      properties: this.currentCitationProperties,
    };
    try {
      await this.referencesService.updateCitation(citationPayload, options);
    } catch (error) {
      console.error('Could not update citation', error);
      this.snackBar.open('Failed to update citation', undefined, { duration: 5000 });
      return;
    }
    if (useFormText) {
      this.setCustomCitationBaseline(
        typeof citationPayload?.plainCitation === 'string' ? citationPayload.plainCitation : '',
        typeof citationPayload?.plainCitationHtml === 'string' ? citationPayload.plainCitationHtml : '',
      );
      this.isCustomTextDirty = false;
    }
  }

  applyFormattedCitationSuggestion(): void {
    if (!this.formattedCitationSuggestion) {
      return;
    }

    const nextText = this.formattedCitationSuggestion;
    this.updateCitationTextForm(nextText, this.escapeHtml(nextText), false);
    this.updateCustomCitationDirtyState();
    this.formattedCitationSuggestion = null;
  }

  discardFormattedCitationSuggestion(): void {
    this.formattedCitationSuggestion = null;
  }

  /** Adds a reference to a citation */
  addReference(reference): void {
    this.citationItems.push(this.createCitationItemGroup({
      id: reference.csl?.id,
      itemData: reference.csl,
      plainReference: reference.bib,
      label: 'page'
    }));
    this.expandedPanelIndices.add(this.citationItems.length - 1);

    this.searchText$.next('');
  }

  /** Returns whether a citation item panel should be expanded. */
  isPanelExpanded(index: number): boolean {
    return this.expandedPanelIndices.has(index);
  }

  /** Tracks panel expand state after user interaction. */
  onPanelOpened(index: number): void {
    this.expandedPanelIndices.add(index);
  }

  /** Tracks panel collapse state after user interaction. */
  onPanelClosed(index: number): void {
    this.expandedPanelIndices.delete(index);
  }

  private createCitationItemGroup(item: CitationItemForUI): CitationItemFormGroup {
    const suppressAuthor: boolean =
      item?.['suppress-author'] === true || (item?.['suppress-author'] as unknown as number) === 1;
    return new FormGroup({
      id: new FormControl<string>(item.id ?? item.itemData?.id ?? '', { nonNullable: true }),
      plainReference: new FormControl<string>(item.plainReference ?? '', { nonNullable: true }),
      label: new FormControl<string>(item.label ?? 'page', { nonNullable: true }),
      locator: new FormControl<string>(item.locator ?? '', { nonNullable: true }),
      prefix: new FormControl<string>(item.prefix ?? '', { nonNullable: true }),
      suffix: new FormControl<string>(item.suffix ?? '', { nonNullable: true }),
      'suppress-author': new FormControl<boolean>(suppressAuthor, { nonNullable: true }),
      format: new FormControl<string>('', { nonNullable: true }),
    });
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  ngAfterViewInit(): void {
    this.setCitationMode(this.citationMode.value === 'custom', false);

    this.citationWithReferences$
      .pipe(distinctUntilKeyChanged('citationID'), takeUntil(this.stop$))
      .subscribe((citation) => {
        this.isHydratingCitationForm = true;

        if (!citation || !Array.isArray(citation.citationItems)) {
          this.citationItems.clear();
          this.resetExpandedPanels();
          this.citationMode.setValue('generated', { emitEvent: false });
          this.setCitationMode(false, false);
          this.updateCitationTextForm('', '', false);
          this.generatedPreviewText = '';
          this.currentCitationProperties = { noteIndex: 0 };
          this.setCustomCitationBaseline('', '');
          this.isCustomTextDirty = false;
          this.formattedCitationSuggestion = null;
          this.isHydratingCitationForm = false;
          return;
        }

        const citationContent = this.referencesService.getCitationContent(citation.citationID);
        const citationText = citationContent?.text ?? citation.plainCitation ?? '';
        const citationHtml = citationContent?.html ?? '';
        const citationMode =
          citationContent?.citationMode ?? citation.citationMode ?? 'generated';
        this.currentCitationProperties = citation.properties ?? { noteIndex: 0 };
        this.previousCitationMode = citationMode;

        this.citationID.setValue(citation.citationID, { emitEvent: false });
        this.citationMode.setValue(citationMode, { emitEvent: false });
        this.setCitationMode(citationMode === 'custom', false);
        this.updateCitationTextForm(citationText, citationHtml, false);
        this.generatedPreviewText = citationText;
        this.setCustomCitationBaseline(citationText, citationHtml);
        this.isCustomTextDirty = false;

        this.citationItems.clear();
        this.resetExpandedPanels();
        for (const item of citation.citationItems) {
          this.citationItems.push(this.createCitationItemGroup(item));
        }

        this.formattedCitationSuggestion = null;
        void this.updateGeneratedPreview();
        this.isHydratingCitationForm = false;
      });

    this.plainCitationHtml.valueChanges
      .pipe(
        filter(() => !this.isHydratingCitationForm),
        filter(() => this.citationMode.value === 'custom'),
        takeUntil(this.stop$),
      )
      .subscribe((htmlValue) => {
        const html = typeof htmlValue === 'string' ? htmlValue : '';
        const plainText = this.toPlainText(html);
        this.plainCitation.setValue(plainText, { emitEvent: false });
        this.updateCustomCitationDirtyState();
      });

    this.citationItems.valueChanges
      .pipe(
        debounceTime(500),
        filter(() => !this.isHydratingCitationForm),
        takeUntil(this.stop$),
      )
      .subscribe(() => {
        void this.updateGeneratedPreview();
        void this.updateFormattedCitationSuggestion();
        void this.applyCitationUpdate({
          preserveNodeContent: this.citationMode.value === 'custom',
        });
      });

    this.citationMode.valueChanges
      .pipe(takeUntil(this.stop$))
      .subscribe((mode) => {
        if (this.isHydratingCitationForm) {
          return;
        }

        this.setCitationMode(mode === 'custom', true);
        this.previousCitationMode = mode;
        void this.updateGeneratedPreview();
        void this.applyCitationUpdate();
      });
  }

  private updateCitationTextForm(text: string, html: string, emitEvent: boolean): void {
    this.plainCitation.setValue(text, { emitEvent });
    this.plainCitationHtml.setValue(html, { emitEvent });
  }

  private toPlainText(html: string): string {
    if (!html) {
      return '';
    }
    if (typeof document === 'undefined') {
      return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    return container.textContent ?? '';
  }

  private async updateFormattedCitationSuggestion(): Promise<void> {
    if (this.isHydratingCitationForm) {
      return;
    }
    if (this.citationMode.value !== 'custom') {
      this.formattedCitationSuggestion = null;
      return;
    }

    const citationItems = this.citationItems.getRawValue();
    if (!Array.isArray(citationItems) || citationItems.length === 0) {
      this.formattedCitationSuggestion = null;
      return;
    }

    const nextFormatted = await this.referencesService.getRenderedCitationText(citationItems);
    const currentText = typeof this.plainCitation.value === 'string' ? this.plainCitation.value : '';
    this.formattedCitationSuggestion =
      nextFormatted && nextFormatted !== currentText ? nextFormatted : null;
  }

  private async updateGeneratedPreview(): Promise<void> {
    if (this.isHydratingCitationForm || this.citationMode.value !== 'generated') {
      return;
    }

    const citationItems = this.citationItems.getRawValue();
    if (!Array.isArray(citationItems) || citationItems.length === 0) {
      this.generatedPreviewText = '';
      return;
    }

    const requestId = ++this.previewRequestId;
    const nextPreview = await this.referencesService.getRenderedCitationText(citationItems);
    if (requestId !== this.previewRequestId) {
      return;
    }

    this.generatedPreviewText = nextPreview ?? '';
  }

  private setCitationMode(enabled: boolean, emitEvent: boolean): void {
    this.ensureCitationTextFormValues();

    if (enabled) {
      this.plainCitationHtml.enable({ emitEvent: false });
      this.updateCustomCitationDirtyState();
      return;
    }
    this.plainCitationHtml.disable({ emitEvent: false });
    if (emitEvent) {
      this.formattedCitationSuggestion = null;
    }
  }

  private setCustomCitationBaseline(text: string, html: string): void {
    this.initialCustomCitationText = text ?? '';
    this.initialCustomCitationHtml = html ?? '';
  }

  private updateCustomCitationDirtyState(): void {
    if (this.citationMode.value !== 'custom') {
      this.isCustomTextDirty = false;
      return;
    }
    const currentText = typeof this.plainCitation.value === 'string' ? this.plainCitation.value : '';
    const currentHtml = typeof this.plainCitationHtml.value === 'string' ? this.plainCitationHtml.value : '';
    this.isCustomTextDirty =
      currentText !== this.initialCustomCitationText || currentHtml !== this.initialCustomCitationHtml;
  }

  private ensureCitationTextFormValues(): void {
    const currentHtml = typeof this.plainCitationHtml.value === 'string' ? this.plainCitationHtml.value : '';
    const currentText = typeof this.plainCitation.value === 'string' ? this.plainCitation.value : '';
    const normalizedHtml = currentHtml || (currentText ? this.escapeHtml(currentText) : '');
    const normalizedText = currentText || this.toPlainText(normalizedHtml);

    if (normalizedHtml !== currentHtml || normalizedText !== currentText) {
      this.updateCitationTextForm(normalizedText, normalizedHtml, false);
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private resetExpandedPanels(): void {
    this.expandedPanelIndices = new Set<number>();
    if (this.citationItems.length > 0) {
      this.expandedPanelIndices.add(0);
    }
  }

  private reindexExpandedPanelsAfterDelete(deletedIndex: number): void {
    const nextExpanded = new Set<number>();
    for (const index of this.expandedPanelIndices) {
      if (index === deletedIndex) {
        continue;
      }
      nextExpanded.add(index > deletedIndex ? index - 1 : index);
    }
    if (nextExpanded.size === 0 && this.citationItems.length > 0) {
      nextExpanded.add(0);
    }
    this.expandedPanelIndices = nextExpanded;
  }

}
